import { MsalService } from '@azure/msal-angular';
import { catchError, from, Observable, switchMap, tap, throwError } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { AccountInfo } from '@azure/msal-browser';
import { CustomIdTokenClaims } from '../models/custom-id-token-claims.model';
import { environment } from '../../../environments/environment';
import { azureClientId } from '../constants/constants';

@Injectable()
export class HttpInterceptor implements HttpInterceptor {
  private readonly msalService = inject(MsalService);

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const account = this.msalService.instance.getActiveAccount();

    if (!account) {
      return next.handle(request);
    }

    if (!this.checkToken(account)) {
      return throwError(() => new Error('Token expired'));
    }

    // Use ID token scopes for refreshing the ID token
    const idTokenScopes = [`api://${azureClientId}/.default`];

    return from(
      this.msalService.instance.acquireTokenSilent({
        scopes: idTokenScopes,
        account,
      }),
    ).pipe(
      switchMap((result) => {
        const idToken = result.accessToken;
        const claims = result.idTokenClaims as CustomIdTokenClaims;

        const headers: Record<string, string> = {
          Authorization: `Bearer ${idToken}`,
        };

        if (claims?.roles) {
          headers['X-User-Roles'] = JSON.stringify(claims?.roles);
        }

        if (claims?.userid) {
          headers['X-User-Id'] = claims.userid;
        }

        const cloned = request.clone({ setHeaders: headers });

        return next.handle(cloned).pipe(
          tap(),
          catchError((err) => {
            return throwError(() => err);
          }),
        );
      }),
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  checkToken(account: AccountInfo) {
    const idTokenClaims = account.idTokenClaims as any;
    const exp = idTokenClaims?.exp;
    const isExpired = exp && Date.now() >= exp * 1000;

    if (isExpired) {
      if (!this.isRedirecting()) {
        this.setRedirecting(true);
        this.msalService.loginRedirect();
      }
      return false;
    } else {
      return true;
    }
  }

  private isRedirecting(): boolean {
    return sessionStorage.getItem('redirecting') === 'true';
  }

  private setRedirecting(flag: boolean): void {
    sessionStorage.setItem('redirecting', flag ? 'true' : 'false');
  }
}
